// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface CollapsibleSectionData {
  title: string;
  content: string;
  type: string; // 'collapsible', 'table', etc.
  format?: string; // For tables: 'csv', 'json', etc.
  isLoading?: boolean; // True when section is still being streamed
}

export interface StreamingMarker {
  type: string;
  completeRegex: RegExp;
  incompleteRegex: RegExp;
  unsafePatterns: string[];
  parseComplete: (match: RegExpExecArray) => CollapsibleSectionData;
  parseIncomplete: (match: RegExpExecArray) => CollapsibleSectionData;
}

// =============================================================================
// STREAMING MARKER DEFINITIONS
// =============================================================================

// Registry of all supported markers - starts empty, markers are registered below
const STREAMING_MARKERS: StreamingMarker[] = [];

// =============================================================================
// EXTENSIBILITY HELPERS
// =============================================================================

/**
 * Factory function to create new streaming markers easily.
 * Use this to add new component types without modifying core logic.
 *
 * SIMPLIFIED APPROACH:
 * - No complex unsafe patterns needed - the main parser catches trailing "<" universally
 * - Much simpler and more robust than enumerating specific patterns
 */
export function createStreamingMarker(
  type: string,
  titleAttribute: string = "title",
  additionalAttributes: Array<{ name: string; optional: boolean }> = [],
): StreamingMarker {
  // Build attribute pattern for regex
  const attrPattern = [
    `${titleAttribute}="([^"]+)"`,
    ...additionalAttributes.map((attr) =>
      attr.optional
        ? `(?:\\s+${attr.name}="([^"]+)")?`
        : `\\s+${attr.name}="([^"]+)"`,
    ),
  ].join("");

  // No need for complex patterns - we rely on trailing "<" detection in main parser
  const tagName = type.toUpperCase();
  const unsafePatterns: string[] = [
    // The main parser handles trailing "<" detection - much simpler!
  ];

  const completeRegex = new RegExp(
    `<<${tagName}\\s+${attrPattern}\\s*>>(.+?)<<\\/${tagName}>>`,
    "gs",
  );

  const incompleteRegex = new RegExp(
    `<<${tagName}\\s+${attrPattern}\\s*>>(.*)$`,
    "gs",
  );

  return {
    type,
    completeRegex,
    incompleteRegex,
    unsafePatterns,
    parseComplete: (match) => {
      const result: CollapsibleSectionData = {
        type,
        title: match[1],
        content: match[match.length - 1].trim(),
        isLoading: false,
      };

      // Add additional attributes if present
      additionalAttributes.forEach((attr, index) => {
        const value = match[2 + index];
        if (value && attr.name === "format") {
          result.format = value;
        }
        // Add more attribute handling as needed
      });

      return result;
    },
    parseIncomplete: (match) => {
      const result: CollapsibleSectionData = {
        type,
        title: match[1],
        content: match[match.length - 1].trim(),
        isLoading: true,
      };

      // Add additional attributes if present
      additionalAttributes.forEach((attr, index) => {
        const value = match[2 + index];
        if (value && attr.name === "format") {
          result.format = value;
        }
        // Add more attribute handling as needed
      });

      return result;
    },
  };
}

/**
 * Add a new streaming marker to the registry at runtime.
 * Useful for plugins or dynamic component registration.
 */
export function registerStreamingMarker(marker: StreamingMarker): void {
  // Check if marker type already exists
  const existingIndex = STREAMING_MARKERS.findIndex(
    (m) => m.type === marker.type,
  );
  if (existingIndex !== -1) {
    // Replace existing marker
    STREAMING_MARKERS[existingIndex] = marker;
  } else {
    // Add new marker
    STREAMING_MARKERS.push(marker);
  }
}

// =============================================================================
// DEFAULT MARKER REGISTRATION
// =============================================================================

// Register the collapsible marker using the factory system
const collapsibleMarker = createStreamingMarker("collapsible");
registerStreamingMarker(collapsibleMarker);

/**
 * Example usage of factory function:
 *
 * // Create a custom chart component
 * const chartMarker = createStreamingMarker('chart', 'title', [
 *   { name: 'type', optional: false },
 *   { name: 'format', optional: true }
 * ]);
 * registerStreamingMarker(chartMarker);
 *
 * // Now you can use: <<CHART title="Sales Data" type="bar" format="json">>...<<\/CHART>>
 * // Trailing "<" detection handles buffering automatically - no complex patterns needed!
 */

// =============================================================================
// CORE GENERIC PARSER
// =============================================================================

/**
 * Generic streaming-safe text parser that handles multiple component types.
 * Simple approach: buffers any trailing '<' that could start component syntax.
 */
export function parseCollapsibleSectionsWithBuffering(text: string): {
  visibleContent: string;
  sections: CollapsibleSectionData[];
  hasBufferedContent: boolean;
} {
  const sections: CollapsibleSectionData[] = [];
  let processedText = text;

  // Phase 1: Extract complete sections
  for (const marker of STREAMING_MARKERS) {
    marker.completeRegex.lastIndex = 0;
    let match;
    while ((match = marker.completeRegex.exec(processedText)) !== null) {
      sections.push(marker.parseComplete(match));
    }
    processedText = processedText.replace(marker.completeRegex, "");
  }

  // Phase 2: Extract incomplete sections
  for (const marker of STREAMING_MARKERS) {
    marker.incompleteRegex.lastIndex = 0;
    const incompleteMatch = marker.incompleteRegex.exec(processedText);
    if (incompleteMatch) {
      const parsedSection = marker.parseIncomplete(incompleteMatch);
      const existingSection = sections.find(
        (s) => s.title === parsedSection.title && s.type === parsedSection.type,
      );
      if (!existingSection) {
        sections.push(parsedSection);
      }
      processedText = processedText.replace(marker.incompleteRegex, "");
    }
  }

  // Phase 3: Stop rendering at the first '<' and show loading
  let visibleContent = processedText;
  let hasBufferedContent = false;

  const firstLessThanIndex = visibleContent.indexOf("<");
  if (firstLessThanIndex !== -1) {
    visibleContent = visibleContent.substring(0, firstLessThanIndex).trim();
    hasBufferedContent = true;
    // NO need to push a fake section — MarkdownText will render the loading indicator
  }

  return {
    visibleContent,
    sections,
    hasBufferedContent,
  };
}
